import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MvCard from "@/components/feature/MvCard";
import Pagination from "@/components/feature/Pagination";
import { genres, mvList } from "@/mocks/mv";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 8;
const regions = ["全部", "华语", "欧美", "日韩"];
const eras = ["全部", "2020年代", "2010年代", "2000年代"];
const sorts = [
  { key: "latest", label: "最新" },
  { key: "hot", label: "最热" },
  { key: "views", label: "播放量" },
];

export default function CategoryPage() {
  const { genre } = useParams();
  const genreInfo = genres.find((g) => g.code === genre);

  const [region, setRegion] = useState("全部");
  const [era, setEra] = useState("全部");
  const [sort, setSort] = useState("latest");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  useSeo({
    title: genreInfo
      ? `红桃MV视频 - 分类 - ${genreInfo.name}`
      : "红桃MV视频 - MV分类",
    description: genreInfo
      ? `红桃MV视频${genreInfo.name}MV分类页面，汇集最新最热的${genreInfo.name}音乐视频作品，${genreInfo.desc}`
      : "红桃MV视频音乐风格分类页面，汇集各类风格的 MV 作品。",
    keywords: genreInfo
      ? `红桃MV视频,${genreInfo.name}MV,${genreInfo.name}音乐视频,MV观看`
      : "红桃MV视频,MV分类,音乐视频",
  });

  const filtered = useMemo(() => {
    let list = mvList.filter((mv) => mv.genreCode === genre);
    if (region !== "全部") list = list.filter((mv) => mv.region === region);
    if (era !== "全部") list = list.filter((mv) => mv.era === era);
    if (sort === "latest") list = [...list].sort((a, b) => (a.releaseDate < b.releaseDate ? 1 : -1));
    if (sort === "hot") list = [...list].sort((a, b) => b.trending - a.trending);
    if (sort === "views") list = [...list].sort((a, b) => b.views - a.views);
    return list;
  }, [genre, region, era, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const applyFilter = (setter: (v: string) => void, value: string) => {
    setter(value);
    setPage(1);
  };

  const resetFilters = () => {
    setRegion("全部");
    setEra("全部");
    setSort("latest");
    setPage(1);
  };

  if (!genreInfo) {
    return (
      <div className="flex w-full flex-col items-center justify-center px-4 py-24 text-center">
        <i className="ri-error-warning-line text-5xl text-secondary-500" aria-hidden="true"></i>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-50">找不到该分类</h1>
        <p className="mt-2 text-sm text-foreground-400">你访问的音乐风格可能已下线，去首页看看其他内容吧。</p>
        <Link to="/" className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
          返回首页
        </Link>
      </div>
    );
  }

  const FilterBlock = (
    <div className="space-y-6">
      <div>
        <h3 className="mb-3 font-heading text-sm font-semibold uppercase tracking-wider text-foreground-100">地区</h3>
        <div className="flex flex-wrap gap-2">
          {regions.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => applyFilter(setRegion, r)}
              className={`cursor-pointer rounded-full px-3 py-1.5 text-xs transition-colors ${
                region === r
                  ? "bg-secondary-500 text-background-950"
                  : "bg-background-800 text-foreground-300 hover:bg-background-700"
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div>
        <h3 className="mb-3 font-heading text-sm font-semibold uppercase tracking-wider text-foreground-100">年代</h3>
        <div className="flex flex-wrap gap-2">
          {eras.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => applyFilter(setEra, e)}
              className={`cursor-pointer rounded-full px-3 py-1.5 text-xs transition-colors ${
                era === e
                  ? "bg-secondary-500 text-background-950"
                  : "bg-background-800 text-foreground-300 hover:bg-background-700"
              }`}
            >
              {e}
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        onClick={resetFilters}
        className="w-full cursor-pointer rounded-md border border-background-700 px-3 py-2 text-sm text-foreground-300 transition-colors hover:border-primary-500 hover:text-primary-400"
      >
        重置筛选
      </button>
    </div>
  );

  return (
    <div className="w-full">
      <div className="border-b border-background-800 bg-background-900/50 px-4 py-8 md:px-8">
        <nav className="mb-3 flex flex-wrap items-center gap-1 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-400">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span>分类</span>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="text-foreground-200">{genreInfo.name}</span>
        </nav>
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-500/15 text-2xl text-primary-400">
            <i className={genreInfo.icon} aria-hidden="true"></i>
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-50 md:text-3xl">{genreInfo.name} MV</h1>
            <p className="mt-1 max-w-2xl text-sm text-foreground-400">{genreInfo.desc}</p>
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col gap-6 px-4 py-8 md:px-8 lg:flex-row">
        <aside className="hidden w-56 shrink-0 lg:block">
          <div className="sticky top-20 rounded-lg border border-background-800 bg-background-900 p-5">
            <h2 className="mb-4 font-heading text-base font-semibold text-foreground-50">筛选</h2>
            {FilterBlock}
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-400">
              共 <span className="text-accent-500">{filtered.length}</span> 支 {genreInfo.name} MV
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFilterOpen((v) => !v)}
                className="flex cursor-pointer items-center gap-1 rounded-md border border-background-700 px-3 py-1.5 text-sm text-foreground-300 lg:hidden"
              >
                <i className="ri-filter-3-line" aria-hidden="true"></i>
                筛选
              </button>
              <div className="flex items-center gap-1 rounded-md border border-background-700 p-1">
                {sorts.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => applyFilter(setSort, s.key)}
                    className={`cursor-pointer rounded px-3 py-1 text-xs transition-colors ${
                      sort === s.key ? "bg-primary-500 text-background-50" : "text-foreground-400 hover:text-foreground-100"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {filterOpen && (
            <div className="mb-4 rounded-lg border border-background-800 bg-background-900 p-5 lg:hidden">
              {FilterBlock}
            </div>
          )}

          {pageItems.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {pageItems.map((mv) => (
                  <MvCard key={mv.id} mv={mv} />
                ))}
              </div>
              <Pagination page={current} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-background-800 bg-background-900 py-20 text-center">
              <i className="ri-search-eye-line text-5xl text-foreground-500" aria-hidden="true"></i>
              <h3 className="mt-4 text-lg font-medium text-foreground-100">没有符合条件的 MV</h3>
              <p className="mt-2 text-sm text-foreground-400">试试调整地区或年代筛选条件</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 cursor-pointer rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                重置筛选
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}